import { Injectable } from '@angular/core';
import {
  USER_ROLE,
  UserInfo as CurUserInfo,
} from '../store/reducers/session-info.reducer';
import { BehaviorSubject } from 'rxjs';
import { UserInfoProvider, UserInfoState } from '@xt/user-context';
import {
  getUserAccountType,
  getUserCategory,
  getUserMfaStatus,
  getUserVerifiedRoles,
  USER_VERIFIED_ROLE,
  UserInfo,
  UserResourceType,
  UserTypeInfo,
} from '@xt/domain-model';
import { shareReplay } from 'rxjs/operators';

const getUserResourceTypes = (userInfo): UserResourceType[] => {
  const idTypes: UserResourceType[] = [];
  // curamenta does not use xt_clinical_record role currently
  if (userInfo.patient_ID && userInfo.roles.includes(USER_ROLE.PATIENT))
    idTypes.push('Patient');
  if (
    userInfo.provider_ID &&
    userInfo.roles.includes(USER_VERIFIED_ROLE.PROVIDER)
  )
    idTypes.push('Practitioner');
  if (
    userInfo.subject &&
    userInfo.roles.includes(USER_VERIFIED_ROLE.REPRESENTATIVE)
  ) {
    idTypes.push('Person');
  }

  return idTypes;
};

export const getUserTypeInfo = (userInfo: UserInfo): UserTypeInfo => {
  return {
    userCategory: getUserCategory(userInfo.roles),
    accountType: getUserAccountType(userInfo.roles),
    authenticationType: userInfo.authentication_type,
    roles: userInfo.roles,
    verifiedRoles: getUserVerifiedRoles(userInfo.roles),
    mfaStatus: getUserMfaStatus(userInfo),
    resourceTypes: getUserResourceTypes(userInfo),
    onboardingStatus: userInfo.onboarding_status,
    isRecoveryMode: userInfo.mfa_availability === 'IN_RECOVERY',
    isGuestLogin: userInfo.is_guest_login ?? false,
  };
};

@Injectable({
  providedIn: 'root',
})
export class CurUserInfoProvider implements UserInfoProvider {
  private userInfoLoaded = false;

  private _state = new BehaviorSubject<UserInfoState>({ state: 'pristine' });

  state$ = this._state.pipe(shareReplay(1));

  setStateLoading() {
    if (this.userInfoLoaded) {
      return;
    }
    this._state.next({ state: 'loading' });
  }

  setUserInfo(userInfo: CurUserInfo) {
    if (this.userInfoLoaded) {
      return;
    }
    this.userInfoLoaded = true;

    const xtUserInfo = this.mapCurUserInfoToXtUserInfo(userInfo);

    this._state.next({
      state: 'loaded',
      userInfo: xtUserInfo,
      userTypeInfo: getUserTypeInfo(xtUserInfo),
    });
  }

  setStateError(error: Error) {
    // if error is thrown, userContext won't try to load
    //this._state.next({ state: 'error', error });
  }

  private mapCurUserInfoToXtUserInfo(userInfo: CurUserInfo): UserInfo {
    if (userInfo.organization_ID) {
      userInfo.organization_ID = userInfo.organization_ID.substring(
        userInfo.organization_ID.lastIndexOf(':') + 1,
      );
    }

    return userInfo as any as UserInfo;
  }
}
